import { Injectable } from '@angular/core';
import { PlaylistInterface } from 'app/models/editorial.model';
import { SystemInterface } from 'app/models/system.model';
import { DateRangeInterface } from 'app/modules/common/datetime/dateslots/dateslot.interface';
import {
  TimeslotInterface,
  TimeslotsCalendarInterface,
  WeeklySchedulingInterface,
} from 'app/modules/common/timeslot/timeslot.interface';
import { PlaylistItemInterface, PlaylistItemNestedInterface } from 'app/modules/editorial/editorial.interface';
import { DataTransformAbstractGet } from 'app/modules/global/classes/data.transform.abstract.up';

@Injectable({
  providedIn: 'root',
})
export class SystemDataTransformGet extends DataTransformAbstractGet {
  transform(system: SystemInterface): SystemInterface {
    // console.log('>>> SYSTEM TRANSFORM UP', system);
    // REMOVE "editorials" field
    /*******/
    if (Array.isArray(system.editorials) && system.editorials.length > 0) {
      if (Array.isArray(system.screens) && system.screens.length > 1) {
        // MULTISCREEN HAS ONLY "playlists", RESET "editorials"
        // console.log('>>> GETTER: MULTISCREEN, READ PLAYLISTS');
      } else {
        // SINGLE SCREEN SHOULD HAVE VALID "editorials"
        // console.log('>>> GETTER: SINGLE SCREEN, MOVE EDITORIALS IN PLAYLISTS');
        system.playlists = system.editorials;
      }
      // ALWAYS CLEAR "editorials"
      system.editorials = [];
    }

    /*******/

    if (system.on) {
      system.on = this.removeUTC(system.on.toString());
    }
    if (system.off) {
      system.off = this.removeUTC(system.off.toString());
    }
    if (system.startAt) {
      system.startAt = this.removeUTC(system.startAt.toString());
    }
    if (system.endAt) {
      system.endAt = this.removeUTC(system.endAt.toString());
    }

    this.normalizePlaylists(system)
      .normalizeMonitor(system)
      .normalizeCalendarScheduling(system)
      .normalizeWeeklyScheduling(system)
      .normalizebrightnessScheduling(system);

    if (Array.isArray(system.screens) && Array.isArray(system.playlists)) {
      // console.log(
      //   '>>> SYSTEM SCREENS',
      //   system?.screens?.map((screen) => JSON.stringify(screen)),
      // );

      // console.log(
      //   '>>> PLAYLISTS SCREENS',
      //   system?.playlists?.map((playlist) => JSON.stringify(playlist.screen)),
      // );

      const screensMap = system.screens.reduce((acc, screen) => {
        acc[screen.guid] = screen;
        return acc;
      }, {});

      system.playlists.forEach((playlist) => {
        if (playlist.screen && screensMap[playlist.screen.guid]) {
          playlist.screen = screensMap[playlist.screen.guid];
        }
      });
    }

    return system;
  }

  private normalizeCalendarScheduling(system: SystemInterface): this {
    system.calendarScheduling = this.calendarSchedulings(system.calendarScheduling);
    return this;
  }

  protected calendarSchedulings(calendarScheduling: TimeslotsCalendarInterface[]): TimeslotsCalendarInterface[] {
    if (!Array.isArray(calendarScheduling)) {
      return calendarScheduling;
    }
    calendarScheduling.forEach((calendar) => {
      if (calendar.startAt) {
        calendar.startAt = this.removeUTC(calendar.startAt.toString());
      }
      if (calendar.endAt) {
        calendar.endAt = this.removeUTC(calendar.endAt.toString());
      }
      calendar.weeklySchedulings = this.normalizeWeeklySchedulings(calendar.weeklySchedulings);
      calendar.timeslots = this.normalizeTimeslots(calendar.timeslots);
    });
    return calendarScheduling;
  }

  protected normalizeTimeslots(timeslots: TimeslotInterface[]) {
    if (!Array.isArray(timeslots)) {
      return timeslots;
    }
    timeslots.forEach((timeslot: TimeslotInterface) => {
      this.normalizeTimeslot(timeslot);
    });
    return timeslots;
  }

  protected normalizeWeeklySchedulings(weeklyScheduling: WeeklySchedulingInterface[]): WeeklySchedulingInterface[] {
    if (Array.isArray(weeklyScheduling)) {
      weeklyScheduling.forEach((day: WeeklySchedulingInterface) => {
        day.timeslots = this.normalizeTimeslots(day.timeslots);
      });
    }
    return weeklyScheduling;
  }

  private normalizePlaylist(playlist: PlaylistInterface) {
    if (!Array.isArray(playlist.resources)) {
      return this;
    }
    playlist.timeslots = this.normalizeTimeslots(playlist.timeslots);
    playlist.resources.forEach((resourceInt: PlaylistItemInterface) => {
      resourceInt.timeslots = this.normalizeTimeslots(resourceInt.timeslots);
      resourceInt.weeklyScheduling = this.normalizeWeeklySchedulings(resourceInt.weeklyScheduling);
      resourceInt.calendarScheduling = this.calendarSchedulings(resourceInt.calendarScheduling);

      this.normalizeNested(resourceInt.nested);
    });
    return this;
  }

  normalizeNested(nested: PlaylistItemNestedInterface) {
    if (nested) {
      if (!Array.isArray(nested.resources)) {
        return this;
      }
      nested.resources.forEach((resourceInt: PlaylistItemInterface) => {
        // THIS SHOULDN'T EXIST IN NESTED RESOURCES
        this.normalizeTimeslots(resourceInt.timeslots);
      });
    }
  }

  normalizeRange(range: DateRangeInterface[]) {
    if (!Array.isArray(range)) {
      return this;
    }
    range.forEach((rangeItem: DateRangeInterface) => {
      if (rangeItem.startAt) {
        rangeItem.startAt = this.removeUTC(rangeItem.startAt.toString());
      }
      if (rangeItem.endAt) {
        rangeItem.endAt = this.removeUTC(rangeItem.endAt.toString());
      }
    });
  }

  private normalizebrightnessScheduling(system: SystemInterface): this {
    if (Array.isArray(system.brightnessScheduling)) {
      system.brightnessScheduling.forEach((item) => {
        if (item.range) {
          this.normalizeTimeslot(item.range);
        }
      });
    }
    return this;
  }

  private normalizeWeeklyScheduling(system: SystemInterface): this {
    system.weeklyScheduling = this.normalizeWeeklySchedulings(system.weeklyScheduling);
    return this;
  }

  private normalizePlaylists(system: SystemInterface): this {
    if (!Array.isArray(system.playlists)) {
      return this;
    }
    system.playlists.forEach((playlist: PlaylistInterface) => {
      this.normalizePlaylist(playlist);
    });
    return this;
  }

  private normalizeMonitor(system: SystemInterface): this {
    if (!system.monitor) {
      return this;
    }
    if (system.monitor.range) {
      this.normalizeMonitorRange(system.monitor.range);
    }
    system.monitor.weeklyScheduling.forEach((day) => {
      this.normalizeTimeslots(day.timeslots);
    });
    return this;
  }

  protected normalizeTimeslot(timeslot: TimeslotInterface): TimeslotInterface {
    if (timeslot.from) {
      timeslot.from = this.removeUTC(timeslot.from.toString());
    }
    if (timeslot.to) {
      timeslot.to = this.removeUTC(timeslot.to.toString());
    }
    return timeslot;
  }

  protected normalizeMonitorRange(range: TimeslotInterface): TimeslotInterface {
    if (range.startAt) {
      range.startAt = this.removeUTC(range.startAt.toString());
    }
    if (range.endAt) {
      range.endAt = this.removeUTC(range.endAt.toString());
    }
    return range;
  }
}
